/**
 * useSessionTimeout - 세션 타임아웃 관리 훅
 *
 * 사용자 비활동 감지 및 세션 만료 경고를 처리합니다.
 *
 * @param {Object} options - 설정 옵션
 * @param {number} options.idleTimeout - 비활동 타임아웃 (ms), 기본값: 25분
 * @param {number} options.warningDuration - 경고 표시 시간 (ms), 기본값: 5분
 * @param {Function} options.onTimeout - 타임아웃 시 호출되는 콜백
 * @param {Function} options.onWarning - 경고 표시 시 호출되는 콜백
 * @param {boolean} options.enabled - 활성화 여부, 기본값: true
 *
 * @returns {Object} - { isWarningVisible, remainingTime, extendSession, cancelWarning }
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULT_IDLE_TIMEOUT = 25 * 60 * 1000; // 25분
const DEFAULT_WARNING_DURATION = 5 * 60 * 1000; // 5분

export const useSessionTimeout = ({
  idleTimeout = DEFAULT_IDLE_TIMEOUT,
  warningDuration = DEFAULT_WARNING_DURATION,
  onTimeout,
  onWarning,
  enabled = true,
} = {}) => {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningDuration);

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // 콜백을 ref로 관리하여 항상 최신 참조 사용 (stale closure 방지)
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);

  // 콜백이 변경될 때마다 ref 업데이트
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
    onWarningRef.current = onWarning;
  }, [onTimeout, onWarning]);

  // 모든 타이머 정리
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // 비활동 타이머 시작
  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();

    idleTimerRef.current = setTimeout(() => {
      // 경고 표시
      setIsWarningVisible(true);
      setRemainingTime(warningDuration);
      onWarningRef.current?.();

      // 카운트다운 시작
      const startTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, warningDuration - elapsed);
        setRemainingTime(remaining);

        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
        }
      }, 1000);

      // 경고 시간 후 타임아웃
      warningTimerRef.current = setTimeout(() => {
        setIsWarningVisible(false);
        onTimeoutRef.current?.();
      }, warningDuration);
    }, idleTimeout);
  }, [idleTimeout, warningDuration, clearAllTimers]);

  // 세션 연장
  const extendSession = useCallback(() => {
    setIsWarningVisible(false);
    setRemainingTime(warningDuration);
    startIdleTimer();
  }, [warningDuration, startIdleTimer]);

  // 경고 취소 (로그아웃 시)
  const cancelWarning = useCallback(() => {
    setIsWarningVisible(false);
    clearAllTimers();
  }, [clearAllTimers]);

  // 상태를 ref로 관리하여 useEffect 의존성 안정화
  const isWarningVisibleRef = useRef(isWarningVisible);
  const startIdleTimerRef = useRef(startIdleTimer);

  // 상태/함수가 변경될 때마다 ref 업데이트
  useEffect(() => {
    isWarningVisibleRef.current = isWarningVisible;
  }, [isWarningVisible]);

  useEffect(() => {
    startIdleTimerRef.current = startIdleTimer;
  }, [startIdleTimer]);

  // 이벤트 리스너 등록
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // 쓰로틀링된 핸들러 (성능 최적화)
    let throttleTimer = null;
    const throttledHandler = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        // ref를 사용하여 항상 최신 상태/함수 참조
        if (isWarningVisibleRef.current) return;
        startIdleTimerRef.current();
        throttleTimer = null;
      }, 1000); // 1초 쓰로틀
    };

    // 이벤트 등록
    events.forEach((event) => {
      document.addEventListener(event, throttledHandler, { passive: true });
    });

    // 초기 타이머 시작
    startIdleTimerRef.current();

    // 클린업
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledHandler);
      });
      if (throttleTimer) clearTimeout(throttleTimer);
      clearAllTimers();
    };
  }, [enabled, clearAllTimers]); // 의존성 최소화

  return {
    isWarningVisible,
    remainingTime,
    extendSession,
    cancelWarning,
  };
};

export default useSessionTimeout;
